import type { Submission } from '../../lib/types'
import { count, pct, mean, sd, f1 } from '../../lib/stats'
import { C } from '../../lib/colors'

interface Props { data: Submission[] }

export function Insights({ data }: Props) {
  const N = data.length
  const dismissed    = count(data, d => ['told-normal', 'told-lose-weight'].includes(d.medical_response ?? ''))
  const thyroidPct   = pct(count(data, d => d.signal_thyroid === 'signalled'), N)
  const noDoctorPct  = pct(count(data, d => d.medical_response === 'not-raised'), N)
  const t3data       = data.filter(d => d.tier === 3)
  const t3Dismiss    = pct(count(t3data, d => ['told-normal', 'told-lose-weight'].includes(d.medical_response ?? '')), t3data.length)
  const pcosGrp      = data.filter(d => d.previous_diagnoses.includes('pcos'))
  const pcosAndrogen = pct(count(pcosGrp, d => d.signal_androgens === 'signalled'), pcosGrp.length)
  const irGrp        = data.filter(d => d.previous_diagnoses.includes('insulin-resistance'))
  const irInsulin    = pct(count(irGrp, d => d.signal_insulin === 'signalled'), irGrp.length)

  const ms = data.map(d => d.score_mood)
  const cs = data.map(d => d.score_cycle)
  const mM = mean(ms), cM = mean(cs)
  const cov = mean(data.map((_, i) => (ms[i] - mM) * (cs[i] - cM)))
  const r = cov / (sd(ms) * sd(cs))
  const cycleMoodCorr = isNaN(r) ? 'N/A' : f1(r)

  void dismissed; void noDoctorPct

  const findings = [
    { cls: '', title: 'Thyroid is the most prevalent signal axis', body: `${thyroidPct}% of the cohort has the Thyroid axis raised — the highest of all four PAInT axes. This is consistent with thyroid disruption being one of the most commonly missed hormonal conditions in reproductive-age women. TSH/T4 appears in the investigation gap of ${pct(count(data, d => d.investigation_gap.includes('TSH/T4')), N)}% of respondents.`, stat: `Thyroid signalled: ${thyroidPct}% of cohort`, statCls: '' },
    { cls: 'f-rose', title: 'Medical dismissal peaks at the most concerned tier', body: `Tier 3 respondents experience a dismissal rate of ${t3Dismiss}% — told normal or told to lose weight despite clinically meaningful symptom loads. This pattern warrants attention in FEMM practitioner training and referral pathway design.`, stat: `Tier 3 dismissal rate: ${t3Dismiss}%`, statCls: 'rose' },
    { cls: 'f-amber', title: 'PCOS and androgen signal co-occurrence', body: `Among respondents with a PCOS diagnosis, ${pcosAndrogen}% have the Androgen axis raised in their FEMM profile. This validates the scoring methodology and suggests that androgen-related symptom patterns are being appropriately captured.`, stat: `PCOS × Androgen signal: ${pcosAndrogen}%`, statCls: 'amber' },
    { cls: 'f-violet', title: 'Insulin resistance and insulin signal co-occurrence', body: `Among respondents with confirmed insulin resistance, ${irInsulin}% have the Insulin axis raised. This cross-validates the Insulin domain questions against known diagnosis.`, stat: `IR × Insulin signal: ${irInsulin}%`, statCls: 'violet' },
    { cls: 'f-blue', title: 'Mood and cycle score correlation', body: `Pearson r = ${cycleMoodCorr} between Mood score and Cycle score across the cohort. Consistent with the known relationship between ovulatory cycle health and progesterone-mediated neurological wellbeing.`, stat: `r = ${cycleMoodCorr} (mood × cycle)`, statCls: 'blue' },
  ]

  return (
    <section className="sec" id="insights">
      <div className="sec-hd">
        <div className="sec-label">08 · Research Insights</div>
        <h2 className="sec-title">What does this cohort reveal?</h2>
        <p className="sec-sub">Computed findings derived directly from the live submission data.</p>
      </div>
      <div className="g3">
        {findings.map((f, i) => (
          <div key={i} className={`finding ${f.cls}`}>
            <div className="f-num">Finding {String(i + 1).padStart(2, '0')}</div>
            <div className="f-title">{f.title}</div>
            <div className="f-body">{f.body}</div>
            <div className={`f-stat ${f.statCls}`}>{f.stat}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// Silence unused color import warning
void C
